import { onlineManager, useQueryClient } from '@tanstack/react-query';
import * as R from 'ramda';
import { MutableRefObject, useCallback, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import usePath from '../../../../components/Hooks/usePath';
import useWidth from '../../../../components/Hooks/useWidth';
import { DataQueryKey, UseDataQueryKey } from '../../../../hooks/useDataQuery/dataQuery.types';
import storage from '../../../../hooks/useStorage/useStorage';
import LangEnum from '../../../../lang/LangEnum';
import PATHS from '../../../../routes/routes.const';
import { getValidateOrderProductsKey } from '../../../../services/modules/order-product/order-product.keys';
import { getFindOrderConditionsKey } from '../../../../services/modules/orderCondition/orderCondition.keys';
import { getTotalProductColorSelloutKey } from '../../../../services/modules/product-color-sellout/product-color-sellout.keys';
import { getFindProductGridKey } from '../../../../services/modules/product-grid/product-grid.keys';
import { getProductDetailsProductColorKey } from '../../../../services/modules/productDetails/productDetails.keys';
import { SuggestedProductsToCustomer } from '../../../../services/modules/products/products.types';
import useOrderBookStore from '../../../../stores/OrderBook/orderBook.store';
import useProductActionsStore from '../../../../stores/ProductActionsStore/productActions.store';
import useProductDetailsStore from '../../../../stores/ProductDetails/productDetails.store';
import useIsAdminSeller from '../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import snackbarStore from '../../../../stores/Snackbar.store';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useLoadingStatusStore } from '../../../context/loadingStatus/LoadingStatus.store';
import useOrderBook from '../../../order-book/hooks/useOrderBook';
import useRelatedMaterials from '../../../orders/products/product/grid/components/orderProductGrid/hooks/useRelatedMaterials';
import { ordersRepository } from '../../../orders/repositories/ordersRepository/orders.repository';
import { useTranslation } from '../../../translation/TranslationContext';
import {
  AvailabilityEnum,
  ESProductPrice,
  ESProductSearch,
  ProductMedia,
} from '../../ProductTypes';
import { isPreOrder } from '../../utils/availability.utils';
import { PriceTypesEnum, ProductDetailsDTO } from '../types/productDetails.types';
import {
  HandleRemoveOrderProductParams,
  IUseProductDetails,
} from '../types/useProductDetailsActions.types';
import { getProductAvailabilitiesToCleanOrder } from './useProductDetailsActions.utils';

const useProductDetailsActions = (): IUseProductDetails => {
  const queryClient = useQueryClient();
  const { translate } = useTranslation();
  const { mobileWidth } = useWidth();

  const { isPath } = usePath();
  const isAdminSeller = useIsAdminSeller();
  const { team } = useApplicationContext();

  const { getRelatedProductColorIds, refetchProductColors } = useRelatedMaterials();
  const { handleMessage } = snackbarStore;
  const { closeOrderBook, allOrders, customerIds } = useOrderBook();
  const handleLoadingStatus = useLoadingStatusStore((state) => state.handleLoadingStatus);

  const [zoomMedia, setZoomMedia] = useState<ProductMedia>();
  const [focusZoomPercentToScroll, setFocusZoomPercentToScroll] = useState<number>(0);

  const {
    finishedOrder,
    productGridParams,
    openedByOrderBook,
    productDetailsModalState,
    setFrom,
    resetFilters,
    resetProductDetailsStore,
    setProductDetailsModalState,
    handleOpenProductDetailsModal,
  } = useProductDetailsStore(
    useShallow((productDetailsState) => ({
      finishedOrder: productDetailsState.finishedOrder,
      productGridParams: productDetailsState.productGridParams,
      openedByOrderBook: productDetailsState.openedByOrderBook,
      productDetailsModalState: productDetailsState.productDetailsModalState,
      setFrom: productDetailsState.setFrom,
      resetFilters: productDetailsState.resetFilters,
      resetProductDetailsStore: productDetailsState.resetProductDetailsStore,
      setProductDetailsModalState: productDetailsState.setProductDetailsModalState,
      handleOpenProductDetailsModal: productDetailsState.handleOpenProductDetailsModal,
    })),
  );

  const { setDeletedProductColors } = useProductActionsStore(
    useShallow((productActionsState) => ({
      setDeletedProductColors: productActionsState.setDeletedProductColors,
    })),
  );

  const { selectedOrderId, reloadOrderItemsQuantity, orderProductsIds, otherOtherProductsIds } =
    useOrderBookStore(
      useShallow((store) => ({
        selectedOrderId: store.selectedOrder?.id,
        reloadOrderItemsQuantity: store.reloadOrderItemsQuantity,
        orderProductsIds: store.orderProductsIds,
        otherOtherProductsIds: store.otherOrdersProductsIds,
      })),
    );

  const availableProductColorId =
    productGridParams.productColorId || productDetailsModalState.productColorId;
  const availableProductId = productDetailsModalState.productId!;
  const availableKitId = productDetailsModalState.kitId;

  const allOrdersSelected = productGridParams?.orderIds?.length;
  const productQueryKey = useMemo(
    () =>
      getProductDetailsProductColorKey({
        productColorId: availableProductColorId!,
        isAdminSeller,
        orderId: selectedOrderId,
      }),
    [availableProductColorId, isAdminSeller, selectedOrderId],
  );

  const product = queryClient.getQueryData<ESProductSearch>([team?.id, ...productQueryKey]);

  const refetchQueriesAfterDeleteOrderItem = async ({
    orderId,
    isSuggested,
    item,
  }: HandleRemoveOrderProductParams) => {
    const mustRefetchRelatedProductColors = isPreOrder(item.availability);
    const relatedProductColorIds = mustRefetchRelatedProductColors
      ? await getRelatedProductColorIds(orderId, [item.productColorId!])
      : item.productColorId;
    if (Array.isArray(relatedProductColorIds) && relatedProductColorIds.length > 0) {
      await refetchProductColors(orderId, relatedProductColorIds);
    } else {
      const queryKey = getFindProductGridKey({
        deliveryWindowId: item.deliveryWindowId,
        availability: item.availability,
        productColorId: item.productColorId!,
        orderId,
        isOfflineMode: !onlineManager.isOnline(),
      });

      await queryClient.refetchQueries({
        queryKey: [team?.id, ...queryKey],
      });
    }

    await queryClient.refetchQueries({
      type: 'active',
      queryKey: [team?.id, DataQueryKey.ORDER_CALCULATE_SUMMARY],
    });
    await Promise.all([
      !!isSuggested &&
        queryClient.refetchQueries({
          queryKey: [
            team?.id,
            DataQueryKey.SUGGESTED_PRODUCTS,
            undefined,
            {
              productIds: [product?.productColorId],
              customerIds,
            },
          ],
        }),
    ]);
  };

  const handleRemoveOrderProduct = useCallback(
    async ({ orderId, isSuggested, item }: HandleRemoveOrderProductParams) => {
      if (!product) {
        return;
      }

      const isSelectedOrder = orderId === selectedOrderId;
      const order = allOrders.find((o) => o.id === orderId);

      if (!order) {
        return;
      }

      await handleLoadingStatus({
        requestFn: () =>
          ordersRepository(orderId).deleteOrderProduct({
            productId: product.productId,
            productColorId: item.productColorId,
            availabilities: getProductAvailabilitiesToCleanOrder(item.availability),
            deliveryWindowId: item.deliveryWindowId ?? undefined,
          }),
        onFailure: ({ code, message }) => {
          const translatedMessage = code && translate(code, { label: message });
          handleMessage({
            type: 'error',
            message: translatedMessage || translate(LangEnum.SNACKBAR_ERROR_REMOVING),
          });
        },
        onSuccess: async () => {
          await refetchQueriesAfterDeleteOrderItem({ orderId, isSuggested, item });
          reloadOrderItemsQuantity(true, orderId, isSelectedOrder);

          if (!isSuggested || order.status !== 'SUGGESTED') {
            return;
          }
          if (isSelectedOrder) {
            setDeletedProductColors(orderProductsIds.productColorIds);
            return;
          }
          const matchingOrder = otherOtherProductsIds?.find((o) => o.orderId === order.id);
          const otherProductsIds = matchingOrder?.orderProductsIds;

          if (otherProductsIds) {
            setDeletedProductColors(otherProductsIds.productColorIds);
          }
        },
      });
    },
    [],
  );

  const verifyProductDetailsModalOpenedByOrderBook = (orderId: string) => {
    const productDetailsFilterSelectedByUser = storage.getProductDetailsFilter(
      storage.getSessionId() as string,
    );
    const isAllColorsFilterSelectedByUser = productDetailsFilterSelectedByUser?.applyAllColors;
    const isRelatedProductsFilterSelectedByUser =
      productDetailsFilterSelectedByUser?.applyRelatedProducts;

    if (openedByOrderBook?.productColorId && openedByOrderBook.state) {
      handleOpenProductDetailsModal(
        {
          ...productGridParams,
          orderIds: [orderId],
          productColorId: isAllColorsFilterSelectedByUser
            ? undefined
            : openedByOrderBook?.productColorId,
        },
        isRelatedProductsFilterSelectedByUser,
      );
      closeOrderBook();
      setProductDetailsModalState({
        isOpen: true,
        productId: openedByOrderBook?.productId,
        productColorId: openedByOrderBook?.productColorId,
      });
    } else if (openedByOrderBook?.kitId && openedByOrderBook.state) {
      handleOpenProductDetailsModal(
        {
          ...productGridParams,
          orderIds: [orderId],
        },
        isRelatedProductsFilterSelectedByUser,
      );
      closeOrderBook();
      setProductDetailsModalState({
        isOpen: true,
        kitId: openedByOrderBook?.kitId,
      });
    }

    setFrom(openedByOrderBook?.from);
  };

  const invalidateOrderGridV2Queries = useCallback(() => {
    if (!selectedOrderId || !productDetailsModalState.kitId) {
      return;
    }

    queryClient.removeQueries({
      predicate: (query) => {
        const queryKey = query.queryKey as ReturnType<typeof getFindProductGridKey>;

        return (
          queryKey[1] === DataQueryKey.PRODUCT_GRID &&
          typeof queryKey[3] === 'object' &&
          queryKey[3].orderId === selectedOrderId
        );
      },
    });
  }, [queryClient, selectedOrderId, productDetailsModalState.kitId]);

  const removeQueriesOnUnmount = useCallback(() => {
    if (isPath(PATHS.main.orders.editForm.path)) {
      queryClient.invalidateQueries({
        queryKey: [team?.id, ...getFindOrderConditionsKey({ orderId: selectedOrderId })],
      });
      queryClient.invalidateQueries({
        queryKey: [team?.id, ...getValidateOrderProductsKey({ orderId: selectedOrderId })],
      });
    }

    queryClient.invalidateQueries({
      queryKey: [team?.id, ...getTotalProductColorSelloutKey({ orderId: selectedOrderId })],
    });

    invalidateOrderGridV2Queries();
    resetFilters();
    resetProductDetailsStore();
  }, [
    isPath,
    queryClient,
    resetFilters,
    invalidateOrderGridV2Queries,
    resetProductDetailsStore,
    team?.id,
    product,
  ]);

  const handleCloseModal = useCallback(() => {
    removeQueriesOnUnmount();
    setProductDetailsModalState({
      isOpen: false,
      productColorId: undefined,
      productId: undefined,
      kitId: undefined,
      disableGrid: undefined,
    });
  }, [removeQueriesOnUnmount, setProductDetailsModalState]);

  const getMinMaxPrice = useCallback(
    (priceType: PriceTypesEnum, prices?: ESProductPrice[] | null) => {
      if (!prices?.length) {
        return;
      }

      const validPrices = prices
        .map((obj) => {
          const price = obj[priceType];
          return !R.isNil(price) && !isNaN(Number(price)) ? Number(price) : null;
        })
        .filter((price): price is number => price !== null);

      if (!validPrices.length) {
        return;
      }

      return {
        min: Math.min(...validPrices),
        max: Math.max(...validPrices),
      };
    },
    [],
  );

  function getFilteredOrder(groupIndex: number) {
    if (productGridParams.isFinishedOrder && finishedOrder) {
      return finishedOrder;
    }

    if (allOrdersSelected) {
      return allOrders[groupIndex];
    }

    const foundOrderId = productGridParams.orderIds?.find((o) => o);

    return allOrders.find((o) => o?.id === foundOrderId);
  }

  const scrollTo = (
    ref: MutableRefObject<HTMLDivElement | null>,
    inline?: ScrollLogicalPosition,
  ) => {
    if (ref.current) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline,
      });
    }
  };

  const getIsSuggestedProduct = useCallback(
    (combination: ProductDetailsDTO, group?: SuggestedProductsToCustomer) => {
      const isSuggested = group?.suggestedProductsInfo.find((suggestedProductsInfo) => {
        const {
          availability: suggestedAvailability,
          deliveryWindowId: suggestedDeliveryWindowId,
          productColorId: suggestedProductColorId,
        } = suggestedProductsInfo;

        const {
          availability: combinationAvailability,
          productColorId: combinationProductColorId,
          deliveryWindowId: combinationDeliveryWindowId,
          futureStockDeliveryWindowId: combinationFutureStockDeliveryWindowId,
        } = combination;

        const deliveryWindowId =
          combinationAvailability === AvailabilityEnum.FUTURE_STOCK
            ? combinationFutureStockDeliveryWindowId
            : combinationDeliveryWindowId;

        const isSameColor = combinationProductColorId === suggestedProductColorId;
        const isSameAvailability = combinationAvailability === suggestedAvailability;
        const isSameDeliveryWindowId = deliveryWindowId === suggestedDeliveryWindowId;

        if (suggestedAvailability === AvailabilityEnum.EMPTY && isSameColor) {
          return true;
        }

        if (isSameColor && isSameAvailability && isSameDeliveryWindowId) {
          return true;
        }

        return false;
      });

      return !!isSuggested;
    },
    [],
  );

  const zoomIn = useCallback(
    (media: ProductMedia, focusPercentToScroll?: number) => {
      setZoomMedia(media);

      if (focusPercentToScroll && !mobileWidth) {
        setFocusZoomPercentToScroll(focusPercentToScroll);
      }
    },
    [mobileWidth],
  );

  const zoomOut = useCallback(() => {
    setZoomMedia(undefined);
    setFocusZoomPercentToScroll(0);
  }, []);

  const getProductDetailsSummaryKey = useCallback(
    (orderId?: string): UseDataQueryKey => [
      team?.id ?? '',
      DataQueryKey.PRODUCT_DETAILS_SUMMARY,
      orderId,
      {
        productColorId: productGridParams.productColorId,
        productId: product?.productId,
        availability: productGridParams.availability,
      },
    ],
    [team?.id, product, productGridParams.productColorId, productGridParams.availability],
  );

  return {
    zoomIn,
    zoomOut,
    scrollTo,
    zoomMedia,
    getMinMaxPrice,
    getFilteredOrder,
    handleCloseModal,
    getIsSuggestedProduct,
    availableKitId,
    availableProductId,
    availableProductColorId,
    focusZoomPercentToScroll,
    handleRemoveOrderProduct,
    getProductDetailsSummaryKey,
    verifyProductDetailsModalOpenedByOrderBook,
  };
};

export default useProductDetailsActions;
